import { createFileRoute, Link } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  FileSpreadsheet,
  Upload,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Dialog } from '@/components/ui/Dialog';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { useOdsImport } from '@/hooks/useOdsImport';
import { formatNumber } from '@/lib/format';
import type { ImportReport } from '@/types/api';

type ImportErrorRow = NonNullable<ImportReport['errors']>[number];
type ImportWarningRow = NonNullable<ImportReport['parse_warnings']>[number];

const IMPORT_ERROR_COLUMNS: ReadonlyArray<Column<ImportErrorRow>> = [
  {
    id: 'sheet',
    header: 'Sheet',
    filter: 'select',
    className: 'font-mono text-xs',
    filterValue: (err) => err.sheet ?? '',
    cell: (err) => err.sheet ?? '–',
  },
  {
    id: 'zeile',
    header: 'Zeile',
    className: 'font-mono text-xs',
    filterValue: (err) => (err.row == null ? '' : String(err.row)),
    cell: (err) => err.row ?? '–',
  },
  {
    id: 'meldung',
    header: 'Meldung',
    filterValue: (err) => err.message ?? '',
    cell: (err) => err.message ?? '–',
  },
];

const IMPORT_WARNING_COLUMNS: ReadonlyArray<Column<ImportWarningRow>> = [
  {
    id: 'sheet',
    header: 'Sheet',
    filter: 'select',
    className: 'font-mono text-xs',
    filterValue: (w) => w.sheet ?? '',
    cell: (w) => w.sheet ?? '–',
  },
  {
    id: 'zeile',
    header: 'Zeile',
    className: 'font-mono text-xs',
    filterValue: (w) => (w.row == null ? '' : String(w.row)),
    cell: (w) => w.row ?? '–',
  },
  {
    id: 'spalte',
    header: 'Spalte',
    filter: 'select',
    className: 'font-mono text-xs',
    filterValue: (w) => w.column ?? '',
    cell: (w) => w.column ?? '–',
  },
  {
    id: 'meldung',
    header: 'Meldung',
    filterValue: (w) => w.message ?? '',
    cell: (w) => w.message ?? '–',
  },
];

/**
 * /settings/import (Sprint 1, ods-Bulk-Import).
 *
 * UX-Strategie:
 *  - Default: dry_run=true, damit der erste Submit immer "safe" ist.
 *  - Sobald dry_run aus -> Re-Confirm-Dialog ("wirklich importieren?").
 *  - Report rendert Counts + Warning-/Error-Tabellen, beides wird im
 *    Dry-Run-Fall genauso geliefert wie im echten Import.
 */

export const Route = createFileRoute('/settings/import')({
  component: ImportPage,
});

function ImportPage(): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [strict, setStrict] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mutation = useOdsImport();

  const triggerImport = (): void => {
    if (!file) return;
    mutation.mutate({ file, dryRun, strict });
  };

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!file) return;
    if (!dryRun) {
      setConfirmOpen(true);
      return;
    }
    triggerImport();
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const f = event.target.files?.[0] ?? null;
    setFile(f);
    mutation.reset();
  };

  const report = mutation.data ?? null;

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/settings"
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-text"
      >
        <ChevronLeft className="size-4" />
        Zurück zu Einstellungen
      </Link>

      <header>
        <h1 className="text-2xl font-semibold">ODS-Import</h1>
        <p className="text-muted">
          Wartungsvertrag-Übersicht.ods einspielen. Default ist Dry-Run – nichts wird
          geschrieben, bis explizit bestätigt.
        </p>
      </header>

      <Card>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <label
              htmlFor="ods-file"
              className="flex flex-col gap-2 text-sm font-medium text-text"
            >
              ODS-Datei (max. 5 MB)
              <input
                id="ods-file"
                ref={fileInputRef}
                type="file"
                accept=".ods,application/vnd.oasis.opendocument.spreadsheet"
                onChange={onFileChange}
                aria-describedby="ods-file-hint"
                className="block w-full rounded-md border border-border bg-surface p-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm hover:file:bg-border/40"
              />
            </label>
            <p id="ods-file-hint" className="mt-1 text-xs text-muted">
              Erwartet: 3 Sheets (planung, server_vm, kunden_vm) gemäß Recherche-Doku.
            </p>
          </div>

          <fieldset className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <legend className="sr-only">Import-Optionen</legend>
            <div className="flex items-start gap-2 text-sm">
              <input
                id="ods-dry-run"
                type="checkbox"
                checked={dryRun}
                onChange={(event) => setDryRun(event.target.checked)}
                className="mt-0.5"
              />
              <label htmlFor="ods-dry-run">
                <span className="font-medium">Dry-Run</span>
                <span className="block text-xs text-muted">
                  Nur Diff-Report, kein Schreiben in die DB.
                </span>
              </label>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <input
                id="ods-strict"
                type="checkbox"
                checked={strict}
                onChange={(event) => setStrict(event.target.checked)}
                className="mt-0.5"
              />
              <label htmlFor="ods-strict">
                <span className="font-medium">Strict</span>
                <span className="block text-xs text-muted">
                  Bei Parse-Fehlern abbrechen statt im Lax-Modus weiter zu importieren.
                </span>
              </label>
            </div>
          </fieldset>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={!file}
              leftIcon={<Upload className="size-4" />}
            >
              {dryRun ? 'Dry-Run starten' : 'Importieren'}
            </Button>
            {file && (
              <span className="inline-flex items-center gap-2 text-sm text-muted">
                <FileSpreadsheet className="size-4" aria-hidden="true" />
                {file.name} ({formatNumber(Math.round(file.size / 1024))} KB)
              </span>
            )}
            {!dryRun && (
              <Badge tone="warning">Schreibend - bitte vorher Backup prüfen</Badge>
            )}
          </div>
        </form>
      </Card>

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {report && <ImportReportView report={report} />}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Wirklich importieren?"
        description="Dry-Run ist deaktiviert. Änderungen werden in die Datenbank geschrieben und im Audit-Log protokolliert."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setConfirmOpen(false);
                triggerImport();
              }}
            >
              Ja, importieren
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Empfehlung: zuerst einen Dry-Run ausführen und das Ergebnis prüfen, bevor du
          schreibend importierst.
        </p>
      </Dialog>
    </div>
  );
}

function ImportReportView({ report }: { report: ImportReport }): JSX.Element {
  const hasWarnings = (report.parse_warnings?.length ?? 0) > 0;
  const hasErrors = (report.errors?.length ?? 0) > 0;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          {hasErrors ? (
            <AlertTriangle className="size-5 text-danger" aria-hidden="true" />
          ) : (
            <CheckCircle2 className="size-5 text-success" aria-hidden="true" />
          )}
          Import-Report
          {report.dry_run && <Badge tone="primary">Dry-Run</Badge>}
        </span>
      }
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Kunden +" value={report.customers_added} tone="success" />
        <Stat label="Kunden ~" value={report.customers_updated} />
        <Stat label="Systeme +" value={report.systems_added} tone="success" />
        <Stat label="Systeme ~" value={report.systems_updated} />
        <Stat label="Profile +" value={report.connection_profiles_added ?? 0} tone="success" />
        <Stat label="Profile ~" value={report.connection_profiles_updated ?? 0} />
        <Stat label="Warnungen" value={report.parse_warnings?.length ?? 0} tone="warning" />
        <Stat label="Fehler" value={report.errors?.length ?? 0} tone="danger" />
      </div>

      {hasErrors && (
        <div className="mt-4">
          <h3 className="mb-2 text-sm font-medium text-danger">Fehler</h3>
          <DataTable
            caption="Import-Fehler"
            columns={IMPORT_ERROR_COLUMNS}
            rows={report.errors ?? []}
            rowKey={(_err, idx) => `err-${idx}`}
            storageKey="import-fehler"
          />
        </div>
      )}

      {hasWarnings && (
        <div className="mt-4">
          <h3 className="mb-2 text-sm font-medium text-warning">Warnungen</h3>
          <DataTable
            caption="Parse-Warnungen"
            columns={IMPORT_WARNING_COLUMNS}
            rows={report.parse_warnings ?? []}
            rowKey={(_warn, idx) => `warn-${idx}`}
            storageKey="import-warnungen"
          />
        </div>
      )}
    </Card>
  );
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'success' | 'warning' | 'danger';
}): JSX.Element {
  const toneClass = {
    neutral: 'text-text',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  }[tone];
  return (
    <div className="rounded-md border border-border bg-surface-2 p-3">
      <div className={`text-xl font-semibold ${toneClass}`}>{formatNumber(value)}</div>
      <div className="text-xs text-muted">{label}</div>
    </div>
  );
}
